import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, ArrowLeft, Store as StoreIcon, Clock, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MapPinIcon, PhoneIcon, ClockIcon } from "@heroicons/react/24/outline";
import { toast } from "sonner";
import { useCart } from "@/contexts/CartContext";
import { cn } from "@/lib/utils";
import { ProductCard } from "@/components/ProductCard";
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Skeleton } from "@/components/ui/skeleton";

interface FloristProfile {
  id: string;
  store_name: string;
  description: string;
  phone: string;
  email: string;
  suburb: string;
  state: string;
  postcode: string;
  address_line1?: string;
  address_line2?: string;
  business_hours: Record<string, { open: string; close: string; closed: boolean }>;
  storefront_image_url: string | null;
  cover_image_url: string | null;
  use_storefront_as_cover: boolean;
  same_day_enabled?: boolean;
  next_day_enabled?: boolean;
  delivery_days?: string[];
  delivery_radius?: number;
  delivery_cutoff_times?: Record<string, string>;
  florist_id: string;
}

interface ProductSize {
  id: string;
  size_name: string;
  price: number;
  main_image_url: string | null;
  gallery_image_urls: string[];
  use_main_product_image: boolean;
  is_active: boolean;
}

interface ProductOccasion {
  occasion_id: string;
  name: string;
}

interface Product {
  id: string;
  store_id: string;
  title: string;
  description: string;
  price: number;
  main_image_url: string | null;
  images: string[];
  is_active: boolean;
  category: string;
  active: boolean;
  created_at: string;
  updated_at: string;
  product_sizes: ProductSize[];
  product_occasions: ProductOccasion[];
}

// Define a type for the expanded product that matches what ProductCard expects
interface ExpandedProduct {
  id: string;
  uniqueId: string;
  title: string;
  description: string;
  price: number;
  sale_price?: number | null;
  images: string[];
  stock_status?: 'in_stock' | 'low_stock' | 'out_of_stock';
  florist: {
    id: string;
    store_name: string;
    suburb: string;
    state: string;
    storefront_image_url: string | null;
    cover_image_url: string | null;
    logo_url?: string;
  };
  selectedVariant: {
    id: string;
    size: string;
    price: number;
  } | null;
  category: string;
  occasions: { id: string; name: string; }[];
  main_image_url: string | null;
  gallery_image_urls?: string[];
  sizes: ProductSize[];
  created_at: string;
  updated_at: string;
  store_id: string;
  active: boolean;
  store: {
    id: string;
    store_name: string;
    description?: string;
    suburb?: string;
    state?: string;
    florist_id: string;
    delivery_radius: number;
    delivery_days: string[];
    store_status: 'active' | 'inactive';
  };
  variants: {
    id: string;
    size: string;
    price: number;
    is_active?: boolean;
  }[];
}

// Transform a product to include variant information
const expandProductSizes = (product: Product, storeName: string, storeDetails: Pick<FloristProfile, 'id' | 'storefront_image_url' | 'cover_image_url' | 'suburb' | 'state'>): ExpandedProduct => {
  const productSizes = product.product_sizes?.filter(size => size.is_active) || [];
  
  // Get the default size (first active size)
  const defaultSize = productSizes[0];
  
  // Create a single product card with all variants
  return {
    id: product.id,
    uniqueId: product.id,
    title: product.title,
    description: product.description || '',
    price: defaultSize?.price || product.price,
    images: product.images?.length > 0 ? product.images : (product.main_image_url ? [product.main_image_url] : []),
    florist: {
      id: storeDetails.id,
      store_name: storeName,
      suburb: storeDetails.suburb || '',
      state: storeDetails.state || '',
      storefront_image_url: storeDetails.storefront_image_url,
      cover_image_url: storeDetails.cover_image_url
    },
    selectedVariant: defaultSize ? {
      id: defaultSize.id,
      size: defaultSize.size_name,
      price: defaultSize.price
    } : null,
    category: product.category || '',
    occasions: product.product_occasions?.map(o => ({ id: o.occasion_id, name: o.name })) || [],
    main_image_url: product.main_image_url,
    gallery_image_urls: [],
    sizes: product.product_sizes || [],
    created_at: product.created_at || new Date().toISOString(),
    updated_at: product.updated_at || new Date().toISOString(),
    store_id: storeDetails.id,
    active: true,
    store: {
      id: storeDetails.id,
      store_name: storeName,
      suburb: storeDetails.suburb,
      state: storeDetails.state,
      florist_id: storeDetails.id,
      delivery_radius: 10, // Default value
      delivery_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      store_status: 'active'
    },
    variants: productSizes.map(size => ({
      id: size.id,
      size: size.size_name,
      price: size.price,
      is_active: size.is_active
    }))
  };
};

export default function FloristProfile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>("all");
  const { addItem } = useCart();
  const [categories, setCategories] = useState<string[]>([]);
  const [expandedProducts, setExpandedProducts] = useState<ExpandedProduct[]>([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  const PRODUCTS_PER_PAGE = 20;
  const LOAD_MORE_COUNT = 10;

  // Fetch florist profile
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ["florist-profile", id],
    queryFn: async () => {
      console.log("Fetching profile for ID:", id); // Debug log
      
      const { data, error } = await supabase
        .from("store_profiles") // Changed back to store_profiles
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        console.error("Profile fetch error:", error); // Debug log
        throw error;
      }
      
      if (!data) {
        throw new Error("Store not found");
      }

      return data as FloristProfile;
    },
  });

  // Fetch florist's products with sizes and occasions
  const { data: queryResult, isLoading: productsLoading } = useQuery({
    queryKey: ["floristProducts", profile?.id, currentPage],
    queryFn: async () => {
      if (!profile?.id) return [];
      
      console.log("Fetching products for store ID:", profile.id);
      
      let productsQuery = supabase
        .from("products")
        .select(`
          *,
          store:store_profiles(*),
          sizes:product_sizes(
            id,
            size_name,
            price,
            is_active,
            main_image_url,
            gallery_image_urls
          ),
          occasions:product_occasions(
            occasion_id,
            occasion:occasions(
              name
            )
          )
        `)
        .eq("store_id", profile.id)
        .eq("active", true)
        .order('created_at', { ascending: false });

      // Add pagination
      const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE;
      const endIndex = startIndex + PRODUCTS_PER_PAGE + 1; // +1 to check if there are more
      productsQuery = productsQuery.range(startIndex, endIndex - 1);

      const { data: productsData, error: productsError } = await productsQuery;

      if (productsError) {
        console.error("Products fetch error:", productsError);
        throw productsError;
      }

      // Debug log to check product sizes
      console.log("Raw products data:", productsData.map(p => ({
        id: p.id,
        title: p.title,
        sizes: p.sizes
      })));

      // Show ONE card per product with lowest price ("From $X")
      const transformedProducts = productsData.map(product => {
        const activeSizes = (product.sizes || []).filter((s: any) => s.is_active);
        const lowestPrice = activeSizes.length > 0
          ? Math.min(...activeSizes.map((s: any) => Number(s.price)))
          : Number(product.price);
        const hasMultiplePrices = activeSizes.length > 1;

        return {
          id: product.id,
          title: product.title,
          description: product.description,
          price: lowestPrice,
          category: product.category,
          images: [product.main_image_url, ...(product.gallery_image_urls || [])].filter((img): img is string => !!img),
          store: {
            id: profile.id,
            store_name: profile.store_name,
            suburb: profile.suburb,
            state: profile.state,
            delivery_radius: profile.delivery_radius,
            storefront_image_url: profile.storefront_image_url,
            cover_image_url: profile.cover_image_url
          },
          showFromPrice: hasMultiplePrices,
          uniqueId: product.id
        };
      });

      // Debug log final transformed products
      console.log("Final transformed products:", transformedProducts.map(p => ({
        id: p.id,
        title: p.title,
        variant: p.selectedVariant
      })));

      // Handle pagination - check if we have more products than requested
      const hasMore = transformedProducts.length > PRODUCTS_PER_PAGE;
      const productsToReturn = hasMore ? transformedProducts.slice(0, PRODUCTS_PER_PAGE) : transformedProducts;
      
      return {
        products: productsToReturn,
        hasMore,
        totalFetched: transformedProducts.length
      };
    },
    enabled: !!profile?.id,
  });

  // Handle pagination state updates
  useEffect(() => {
    if (queryResult) {
      if (currentPage === 1) {
        // First page - replace all products
        setAllProducts(queryResult.products || []);
      } else {
        // Additional pages - append to existing products
        setAllProducts(prev => [...prev, ...(queryResult.products || [])]);
      }
      setHasMoreProducts(queryResult.hasMore || false);
      setIsLoadingMore(false);
    }
  }, [queryResult, currentPage]);

  // Reset pagination when profile changes
  useEffect(() => {
    setCurrentPage(1);
    setAllProducts([]);
    setHasMoreProducts(true);
  }, [profile?.id]);

  // Load More function
  const handleLoadMore = () => {
    if (!isLoadingMore && hasMoreProducts) {
      setIsLoadingMore(true);
      setCurrentPage(prev => prev + 1);
    }
  };

  // Use allProducts for display instead of direct query result
  const products = allProducts;

  const handleAddToCart = (product: Product, size?: ProductSize) => {
    if (!product.is_active) {
      toast.error("This product is currently unavailable");
      return;
    }

    if (product.product_sizes.length > 0 && !size) {
      toast.error("Please select a size");
      return;
    }

    addItem({
      id: product.id,
      title: product.title,
      price: size ? size.price : product.price,
      image: size?.main_image_url || product.main_image_url || product.images[0],
      floristId: profile?.florist_id,
      floristName: profile?.store_name,
      sizeId: size?.id,
      sizeName: size?.size_name,
    });
    toast.success("Added to cart!");
  };

  const filteredProducts = products?.filter(product => {
    if (activeTab === "all") return true;
    
    // Find the original product to check its category
    const originalProduct = products?.find(p => p.id === product.id);
    return originalProduct?.category.toLowerCase() === activeTab.toLowerCase();
  });

  if (profileLoading || productsLoading) {
    return (
      <div className="min-h-screen bg-brand-accent">
        <div className="container mx-auto py-12">
          <div className="max-w-[1400px] mx-auto px-4 space-y-6">
            <Skeleton className="h-5 w-48" />
            <div className="bg-white rounded-xl p-8 shadow-sm space-y-6">
              <Skeleton className="h-10 w-64" />
              <div className="flex gap-6">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-5 w-32" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Skeleton className="h-64 w-full rounded-lg" />
                <Skeleton className="h-64 w-full rounded-lg" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Skeleton className="h-48 w-full rounded-lg" />
                <Skeleton className="h-48 w-full rounded-lg" />
                <Skeleton className="h-48 w-full rounded-lg" />
              </div>
            </div>
            <Skeleton className="h-8 w-32" />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden border border-black/10">
                  <Skeleton className="aspect-square w-full" />
                  <div className="p-3 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                    <Skeleton className="h-8 w-full rounded-full" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-brand-accent flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm p-8 max-w-md w-full mx-4 border border-brand-primary/10">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-brand-primary mb-2">Store Not Found</h2>
            <p className="text-sm text-brand-primary/70 mb-4">The store you're looking for doesn't exist or has been removed.</p>
            <Link 
              to="/"
              className="inline-flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-medium text-white bg-[#967F81] hover:bg-[#843329] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
            >
              Return Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Define semantic image variables for clarity
  const profileSquareImage = profile.storefront_image_url;
  const storefrontImage = profile.cover_image_url;
  
  // Format the full address
  const addressParts = [
    profile.address_line1,
    profile.address_line2,
    profile.suburb,
    profile.state,
    profile.postcode
  ].filter(Boolean);
  
  const fullAddress = addressParts.join(', ');

  return (
    <div className="min-h-screen bg-brand-accent">
      {/* Breadcrumb Navigation removed as requested */}

      <div className="container mx-auto py-12">
        <div className="max-w-[1400px] mx-auto px-4">
          {/* Breadcrumb Navigation */}
          <Breadcrumb className="mb-6">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild><Link to="/search">Search</Link></BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{profile.store_name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {/* Store Header with Information */}
          <div className="bg-white rounded-xl shadow-sm border border-brand-primary/10 overflow-hidden mb-8">            
            <div className="p-8">
              {/* Store Name and Info */}
              <div className="mb-6">
                <h1 className="text-3xl md:text-4xl font-semibold text-black mb-4">
                  {profile.store_name}
                </h1>
                
                <div className="flex flex-wrap gap-6 text-sm text-gray-700">
                  <div className="flex items-center gap-2">
                    <MapPinIcon className="h-5 w-5 text-brand-primary" />
                    <span>{fullAddress}</span>
                  </div>
                  {profile.phone && (
                    <div className="flex items-center gap-2">
                      <PhoneIcon className="h-5 w-5 text-brand-primary" />
                      <span>{profile.phone}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Enhanced Image Layout - Side by Side with 1/3 - 2/3 split */}
              <div className="flex flex-col md:flex-row h-auto md:h-[400px] overflow-hidden rounded-xl shadow-sm md:gap-6 bg-brand-accent/30">
                {/* Side-by-side images on mobile */}
                <div className="w-full md:w-1/3 h-[240px] md:h-full p-3 flex-shrink-0">
                  {profileSquareImage ? (
                    <div className="w-full h-full relative rounded-lg overflow-hidden">
                      <img 
                        src={profileSquareImage} 
                        alt={`${profile.store_name} profile`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-full bg-[#967F81]/10 flex items-center justify-center rounded-lg">
                      <StoreIcon className="w-16 h-16 text-brand-primary/50" />
                    </div>
                  )}
                </div>
                
                {/* Landscape Storefront Image - 2/3 width */}
                <div className="w-full md:w-2/3 h-[240px] md:h-full p-3">
                  {storefrontImage ? (
                    <div className="w-full h-full relative rounded-lg overflow-hidden">
                      <img 
                        src={storefrontImage} 
                        alt={`${profile.store_name} storefront`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-l from-black/30 to-transparent"></div>
                    </div>
                  ) : (
                    <div className="w-full h-full bg-[#967F81]/10 flex items-center justify-center rounded-lg">
                      <StoreIcon className="w-20 h-20 text-brand-primary/30" />
                    </div>
                  )}
                </div>
              </div>

              {/* Delivery Information Bubbles - More prominent on mobile */}
              <div className="flex flex-wrap gap-3 mt-6 md:hidden">
                {profile.delivery_radius && (
                  <div className="inline-flex items-center px-5 py-2.5 rounded-full bg-[#967F81]/10 text-sm font-medium text-black">
                    <Clock className="h-4 w-4 mr-2 text-brand-primary" />
                    {profile.delivery_radius}km Radius
                  </div>
                )}
                
                {profile.same_day_enabled && (
                  <div className="inline-flex items-center px-5 py-2.5 rounded-full bg-[#967F81]/10 text-sm font-medium text-black">
                    <Clock className="h-4 w-4 mr-2 text-brand-primary" />
                    Same-Day Available
                  </div>
                )}
                
                {profile.delivery_cutoff_times && 
                Object.keys(profile.delivery_cutoff_times).length > 0 && (
                  <div className="inline-flex items-center px-5 py-2.5 rounded-full bg-[#967F81]/10 text-sm font-medium text-black">
                    <Clock className="h-4 w-4 mr-2 text-brand-primary" />
                    Cutoff: {Object.values(profile.delivery_cutoff_times)[0]}
                  </div>
                )}
                
                {profile.delivery_days && profile.delivery_days.length > 0 && (
                  <div className="inline-flex items-center px-5 py-2.5 rounded-full bg-[#967F81]/10 text-sm font-medium text-black">
                    <Clock className="h-4 w-4 mr-2 text-brand-primary" />
                    Days: {profile.delivery_days.map(day => 
                      day.charAt(0).toUpperCase() + day.slice(1).substring(0, 3)
                    ).join(", ")}
                  </div>
                )}
              </div>

              {/* Three Equal Boxes Layout for About, Hours, and Delivery */}
              <div className="grid grid-cols-1 md:grid-cols-3 mt-6 gap-4 md:gap-6">
                {/* About Section - Takes 1/3 on desktop, full width on mobile */}
                {profile.description && (
                  <div className="bg-white rounded-lg p-5 shadow-sm">
                    <h2 className="text-lg font-medium text-black mb-3">About</h2>
                    <p className="text-[15px] text-gray-700 leading-relaxed">
                      {profile.description}
                    </p>
                  </div>
                )}

                {/* Operating Hours Summary - Takes 1/3 on desktop, full width on mobile */}
                <div className="bg-white rounded-lg p-5 shadow-sm">
                  <h2 className="text-lg font-medium text-black mb-3 flex items-center">
                    <ClockIcon className="h-5 w-5 mr-2 text-brand-primary" />
                    Operating Hours
                  </h2>
                  <div className="space-y-1">
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
                      .filter(day => profile.business_hours[day])
                      .map(day => {
                        const hours = profile.business_hours[day];
                        return (
                          <div key={day} className="flex justify-between py-2 border-b border-brand-primary/5 last:border-0">
                            <span className="text-[15px] text-gray-700">{day.charAt(0).toUpperCase() + day.slice(1)}</span>
                            <span className="text-[15px] font-medium text-black">
                              {hours.closed ? 'Closed' : `${hours.open} - ${hours.close}`}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* Delivery Information - Takes 1/3 on desktop, full width on mobile */}
                <div className="bg-white rounded-lg p-5 shadow-sm">
                  <h2 className="text-lg font-medium text-black mb-3 flex items-center">
                    <Clock className="h-5 w-5 mr-2 text-brand-primary" />
                    Delivery Information
                  </h2>
                  <div className="space-y-2">
                    {profile.delivery_radius && (
                      <div className="flex items-center px-3 py-2 rounded-md bg-brand-accent text-[14px] text-black">
                        <Clock className="h-4 w-4 mr-2 text-brand-primary" />
                        <span>{profile.delivery_radius}km Delivery Radius</span>
                      </div>
                    )}
                    
                    {profile.same_day_enabled && (
                      <div className="flex items-center px-3 py-2 rounded-md bg-brand-accent text-[14px] text-black">
                        <Clock className="h-4 w-4 mr-2 text-brand-primary" />
                        <span>Same-Day Delivery Available</span>
                      </div>
                    )}
                    
                    {profile.delivery_cutoff_times && 
                    Object.keys(profile.delivery_cutoff_times).length > 0 && (
                      <div className="flex items-center px-3 py-2 rounded-md bg-brand-accent text-[14px] text-black">
                        <Clock className="h-4 w-4 mr-2 text-brand-primary" />
                        <span>Same-Day Cutoff: {Object.values(profile.delivery_cutoff_times)[0]}</span>
                      </div>
                    )}
                    
                    {profile.delivery_days && profile.delivery_days.length > 0 && (
                      <div className="flex items-center px-3 py-2 rounded-md bg-brand-accent text-[14px] text-black">
                        <Clock className="h-4 w-4 mr-2 text-brand-primary" />
                        <span>
                          Delivery Days: {profile.delivery_days.map(day => 
                            day.charAt(0).toUpperCase() + day.slice(1)
                          ).join(", ")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Product Categories and Grid */}
          <div className="bg-white rounded-xl shadow-sm border border-brand-primary/10 p-4 md:p-8">
            {/* Category Tabs */}
            <h2 className="text-xl font-semibold text-black mb-4 md:mb-6">Products</h2>
            <div className="flex flex-wrap gap-2 mb-6 md:mb-8 overflow-x-auto pb-2 scrollbar-none">
              <Button
                variant={activeTab === "all" ? "default" : "outline"}
                onClick={() => setActiveTab("all")}
                className={cn(
                  "shrink-0 text-[14px] px-4 py-2 rounded-full", 
                  activeTab === "all" 
                    ? "bg-[#967F81] text-white hover:bg-[#843329]" 
                    : "border-brand-primary text-black hover:bg-[#843329]"
                )}
              >
                All Products
              </Button>
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={activeTab === category ? "default" : "outline"}
                  onClick={() => setActiveTab(category)}
                  className={cn(
                    "shrink-0 text-[14px] px-4 py-2 rounded-full", 
                    activeTab === category 
                      ? "bg-[#967F81] text-white hover:bg-[#843329]" 
                      : "border-brand-primary text-black hover:bg-[#843329]"
                  )}
                >
                  {category}
                </Button>
              ))}
            </div>

            {/* Product Grid - Consistent with Search Page */}
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {products && products.length > 0 ? (
                products.map((product) => (
                  <ProductCard
                    key={product.uniqueId || product.id}
                    product={product}
                    className="h-full"
                    showStoreName={false}
                    isFloristCard={true}
                    showFromPrice={product.showFromPrice}
                  />
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-accent mb-4">
                    <ShoppingCart className="h-8 w-8 text-brand-primary/50" />
                  </div>
                  <h3 className="text-lg font-medium text-black mb-2">
                    No products available
                  </h3>
                  <p className="text-gray-600 max-w-md mx-auto">
                    This florist doesn't have any products available at the moment. Please check back later.
                  </p>
                </div>
              )}
            </div>
            
            {/* Load More Button */}
            {products && products.length > 0 && hasMoreProducts && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-[#967F81] text-white rounded-full font-medium hover:bg-[#843329] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingMore ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading more...
                    </>
                  ) : (
                    <>
                      Load More Products
                      <span className="text-sm opacity-80">({LOAD_MORE_COUNT} more)</span>
                    </>
                  )}
                </button>
              </div>
            )}
            
            {/* Products count indicator */}
            {products && products.length > 0 && (
              <div className="text-center mt-4 text-sm text-gray-600">
                Showing {products.length} products
                {!hasMoreProducts && products.length > PRODUCTS_PER_PAGE && (
                  <span className="ml-1">• All products loaded</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 